import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Avatar,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import Insights from '@mui/icons-material/Insights';
import Public from '@mui/icons-material/Public';
import MarketStatusChip from '../../design/MarketStatusChip';
import { appBarGradient } from '../../design/theme';
import { useAuth } from '../../context/AuthContext';
import { useOverview, useEquityCurves } from '../../hooks/useHomeData';
import { PerformanceHistoryItem } from '../../hooks/useStatistics';
import TraderCard from './TraderCard';

type Range = '7d' | '30d' | 'all';

// Compute a start_date N days back (YYYY-MM-DD), mirroring Statistics.tsx.
const startDateFor = (range: Range): string | undefined => {
  if (range === 'all') return undefined;
  const days = range === '7d' ? 7 : 30;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
};

// Null NLA sorts last, otherwise descending by net liquid assets.
const byNlaDesc = (a: { net_liquid_assets: number | null }, b: { net_liquid_assets: number | null }): number => {
  if (a.net_liquid_assets === null && b.net_liquid_assets === null) return 0;
  if (a.net_liquid_assets === null) return 1;
  if (b.net_liquid_assets === null) return -1;
  return b.net_liquid_assets - a.net_liquid_assets;
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  // 'all' default keeps the cards' Realized consistent with the all-time
  // Realized P&L on the per-trader Overview tab.
  const [range, setRange] = useState<Range>('all');

  const startDate = useMemo(() => startDateFor(range), [range]);
  const endDate = useMemo(
    () => (range === 'all' ? undefined : new Date().toISOString().split('T')[0]),
    [range],
  );

  const overview = useOverview(startDate, endDate);
  const equity = useEquityCurves(startDate, endDate);

  // Map each trader's equity curve by id so a card can pull its own sparkline.
  // An equity-curves failure just leaves this empty — cards render without sparklines.
  const sparkById = useMemo(() => {
    const map = new Map<number, PerformanceHistoryItem[]>();
    (equity.data?.curves ?? []).forEach((c) => map.set(c.trader_id, c.data));
    return map;
  }, [equity.data]);

  const sortedTraders = useMemo(
    () => [...(overview.data?.traders ?? [])].sort(byNlaDesc),
    [overview.data],
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleRange = (_e: React.MouseEvent<HTMLElement>, next: Range | null) => {
    if (next) setRange(next);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{ background: appBarGradient }}
      >
        <Toolbar>
          <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ p: 0, mr: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <Insights />
            </Avatar>
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Shadow Trader
          </Typography>
          <IconButton color="inherit" onClick={() => navigate('/universe')} aria-label="Stock universe" sx={{ mr: 1 }}>
            <Public />
          </IconButton>
          <Box sx={{ mr: 1 }}>
            <MarketStatusChip />
          </Box>
          <IconButton color="inherit" onClick={handleLogout} aria-label="Log out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark' }}>
            Traders
          </Typography>
          <ToggleButtonGroup value={range} exclusive onChange={handleRange} size="small" aria-label="Time range">
            <ToggleButton value="7d">7D</ToggleButton>
            <ToggleButton value="30d">30D</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {overview.error ? (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 2, px: 2, py: 1.5, borderRadius: 4,
              border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
            }}
          >
            <Typography color="error">{overview.error}</Typography>
            <Button size="small" onClick={overview.refetch}>Retry</Button>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {overview.loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <Grid item xs={12} sm={6} lg={3} key={i}>
                    <Skeleton variant="rounded" height={230} />
                  </Grid>
                ))
              : sortedTraders.map((row) => (
                  <Grid item xs={12} sm={6} lg={3} key={row.trader_id}>
                    <TraderCard row={row} spark={sparkById.get(row.trader_id)} />
                  </Grid>
                ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Home;
